import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface HeroHeadingProps {
  children: ReactNode
  className?: string
}

interface HighlightProps {
  children: ReactNode
  className?: string
}

/**
 * HeroHeading Component
 *
 * Componente de título principal do Hero com suporte a destaques.
 * Use HeroHeading.Highlight para palavras com fundo verde.
 *
 * @example
 * ```tsx
 * <HeroHeading>
 *   Sua carreira de Creator,{" "}
 *   <HeroHeading.Highlight>Organizada.</HeroHeading.Highlight>{" "}
 *   <HeroHeading.Highlight>Profissional.</HeroHeading.Highlight>{" "}
 *   <HeroHeading.Highlight>Rentável.</HeroHeading.Highlight>
 * </HeroHeading>
 * ```
 */
export function HeroHeading({ children, className }: HeroHeadingProps) {
  return (
    <h1
      className={cn(
        'text-4xl md:text-5xl lg:text-6xl',
        'text-tami-black',
        'tracking-tight',
        className
      )}
    >
      {children}
    </h1>
  )
}

/**
 * HeroHeading.Highlight Component
 *
 * Subcomponente para destacar palavras com efeito de marca-texto verde embaixo.
 * Usa box-shadow para criar o efeito de sublinhado/highlight.
 * Deve ser usado dentro de HeroHeading.
 */
function Highlight({ children, className }: HighlightProps) {
  return (
    <span
      className={cn(
        'italic',
        'relative',
        'inline-block',
        className
      )}
      style={{
        boxShadow: `inset 0 -0.5em 0 0 var(--tami-green)`,
      }}
    >
      {children}
    </span>
  )
}

// Adiciona Highlight como propriedade do HeroHeading
HeroHeading.Highlight = Highlight
